import type { SubscriptionDetails } from "schemas/generated/billing";

import * as React from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { workspaceRouter } from "@editor/trpc/workspace";

import { Card } from "@replo/design-system/components/shadcn/core/card";
import { skipToken } from "@tanstack/react-query";
import { getV2BillingPlanInfoByTier } from "schemas/billing";

import BillingIntervalToggle from "../shared/BillingIntervalToggle";
import PlanCard from "../shared/PlanCard";
import SubscriptonCancelledNotEndedAlert from "../shared/SubscriptonCancelledNotEndedAlert";
import V2BillingLimitsCard from "./V2BillingLimitsCard";

const V2Billing = ({
  activeSubscription,
  isYearly,
  setIsYearly,
}: {
  activeSubscription?: SubscriptionDetails;
  isYearly: boolean;
  setIsYearly: (isYearly: boolean) => void;
}) => {
  const workspace = useCurrentWorkspace();

  const isWorkspaceOwner = useIsWorkspaceOwner(workspace?.id);

  const { data: v2ShopsUsage } = workspaceRouter.getV2ShopsUsage.useQuery(
    !workspace?.id ? skipToken : workspace.id,
  );

  const { data: chatMessagesUsage } =
    workspaceRouter.getChatMessagesUsage.useQuery(
      !workspace?.id ? skipToken : workspace.id,
    );

  const currentPlan = activeSubscription?.v2BillingTier ?? "free";

  if (!workspace) {
    return null;
  }

  const plans = [
    getV2BillingPlanInfoByTier("starter"),
    getV2BillingPlanInfoByTier("pro"),
    getV2BillingPlanInfoByTier("custom_v2"),
  ] as const;

  return (
    <div className="flex w-full flex-col gap-6">
      <V2BillingLimitsCard
        shopUsage={{
          current: v2ShopsUsage?.current ?? 0,
          maximum: v2ShopsUsage?.maximum ?? null,
        }}
        monthlyMessagesUsage={{
          current: chatMessagesUsage?.current ?? 0,
          maximum: chatMessagesUsage?.maximum ?? null,
        }}
        activeSubscription={activeSubscription ?? undefined}
        workspaceId={workspace.id}
        isWorkspaceOwner={isWorkspaceOwner}
      />
      <SubscriptonCancelledNotEndedAlert
        activeSubscription={activeSubscription}
        workspaceId={workspace.id}
        reploVersion="v2"
      />

      <Card className="flex flex-col gap-8">
        <div className="flex justify-between items-center">
          <div className="header-h3">
            Compare {isYearly ? "Annual" : "Monthly"} Plans
          </div>
          <BillingIntervalToggle
            isYearly={isYearly}
            setIsYearly={setIsYearly}
            isWorkspaceOnYearlySubscription={
              activeSubscription?.billingInterval === "yearly"
            }
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <PlanCard
              key={plan.tier}
              reploVersion="v2"
              plan={plan}
              currentPlan={currentPlan}
              isYearly={isYearly}
              workspace={workspace}
              source="direct.billingDashboardManage"
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

export default V2Billing;
